// 오디오 엔진: 게임 이벤트를 자기 커서로 읽어 효과음을 내고, 장면에 맞춰 BGM을 교차 페이드한다(docs/AUDIO.md).
// 히트스톱·각성 슬로모션과 무관하게 실시간으로 울린다(COMBAT-FEEL 2.1). 재생 경로는 미리 만든 목소리 24개를 돌려 쓰고,
// 소리마다 새로 만드는 것은 WebAudio 규칙상 일회용인 AudioBufferSourceNode 하나뿐이다.
import { Ev, type GameEvent } from '../shared/events';
import { AwakenPhase, MobState, Scene, combatScene, type GameView } from '../shared/types';
import { AmbiencePlayer } from './ambience';
import { ALL_TRACKS, BgmPlayer, chooseTrack, liveMusic, liveTrackOf, regionOfResult } from './bgm';
import { CueList, decideEvent, distanceDb, jitterRate, panFor, Sweep, type Cue, type DecideState } from './decide';
import { dbToGain, duckDb, impactDuckDb, impactDucks } from './duck';
import { GAIN_JITTER_DB, MAX_SAME_SFX, pickLoadedVariant, variantCount, variantFile } from './variants';
import { VoiceLimiter } from './limiter';
import { audioVolumes, onVolumeChange, setAudioVolume, volumeToGain, type AudioVolumes, type VolumeKind } from './settings';
import { GROUP_LIMITS, Group, S, SFX_BY_ID, SFX_NAMES, isBigGroup } from './sounds';
import { UI_SLOT_FILES, UI_SLOTS, isUiSlot, uiSlotPath, type UiSlotName } from './uiSlots';
import { SynthEngine } from './synth/engine';
import { lyraCueOf } from './synth/lyraSfx';
import { isHeavy } from './synth/decide';
import { RaidAudioEngine } from './raid/engine';
import { isWyvernEvent } from './raid/decide';
import { resolveAssetUrl } from '../pwa/assetMap';

export type { AudioVolumes, VolumeKind };

export interface AudioHandle {
  /** 렌더 프레임마다 호출(frameDt 초) */
  update(frameDt: number): void;
  setVolume(kind: VolumeKind, v: number): void;
  volumes(): Readonly<AudioVolumes>;
}

export interface AudioOptions {
  /** window.__DHA__ QA 훅(재생 통계·레벨 측정·녹음)을 켠다 */
  qa?: boolean;
  /** 효과음 폴더(기본 'assets/audio/sfx/') */
  sfxBase?: string;
  /** 이 약속이 풀릴 때까지 BGM·효과음 내려받기를 미룬다(인트로가 떠 있는 동안 첫 화면 자원에 회선을 준다) */
  deferNetwork?: Promise<void>;
}

const VOICES = 24;
const UNLOCK_EVENTS = ['pointerdown', 'keydown', 'touchstart'] as const;
const CONFIRM_IDS = new Set(['start', 'resume', 'to-village', 'retry', 'defeat-retry', 'defeat-village', 'settings-close']);

export function createAudio(view: GameView, opts: AudioOptions = {}): AudioHandle {
  const W = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
  const Ctor = W.AudioContext ?? W.webkitAudioContext;
  if (!Ctor) {
    // 오디오를 못 쓰는 환경: 설정만 저장한다
    return { update() {}, setVolume: setAudioVolume, volumes: audioVolumes };
  }
  const ctx = new Ctor({ latencyHint: 'interactive' });

  // ── 버스: 효과음 → 마스터, BGM → 덕킹 → 마스터, 마스터 → 안전 리미터 → 출력 여유(-1.5dB) → 출력
  const master = ctx.createGain();
  const sfxBus = ctx.createGain();
  const bgmBus = ctx.createGain();
  const duck = ctx.createGain();
  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -10;
  limiter.knee.value = 6;
  limiter.ratio.value = 20;
  limiter.attack.value = 0.001;
  limiter.release.value = 0.1;
  const trim = ctx.createGain();
  trim.gain.value = dbToGain(-1.5);
  sfxBus.connect(master);
  bgmBus.connect(duck);
  duck.connect(master);
  master.connect(limiter);
  limiter.connect(trim);
  trim.connect(ctx.destination);

  const applyVolumes = (): void => {
    const v = audioVolumes();
    master.gain.value = volumeToGain(v.master);
    sfxBus.gain.value = volumeToGain(v.sfx);
    bgmBus.gain.value = volumeToGain(v.bgm);
  };
  applyVolumes();

  // ── 효과음 버퍼(58개, 합계 2MB 미만)
  const buffers: (AudioBuffer | null)[] = new Array<AudioBuffer | null>(SFX_NAMES.length).fill(null);
  // 변형(variants.ts): 0번은 buffers와 같다. 늦게 온 변형은 들어오는 대로 고를 수 있게 된다
  const vbufs: AudioBuffer[][] = SFX_NAMES.map(() => []);
  const lastVariant = new Int8Array(SFX_NAMES.length).fill(-1);
  const base = opts.sfxBase ?? 'assets/audio/sfx/';
  let loaded = 0, loadFailed = 0;
  /** 내려받기 허용(deferNetwork가 풀리기 전에는 BGM 트랙도 고르지 않는다) */
  let netOpen = !opts.deferNetwork;
  const loadSfx = (): void => {
    SFX_NAMES.forEach((name, i) => {
      const n = variantCount(name);
      for (let v = 0; v < n; v++) {
        fetch(resolveAssetUrl(base + variantFile(name, v)))
          .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.arrayBuffer(); })
          .then((b) => ctx.decodeAudioData(b))
          .then((buf) => { if (v === 0) { buffers[i] = buf; loaded++; } vbufs[i][v] = buf; })
          .catch(() => { if (v === 0) loadFailed++; });
      }
    });
  };
  // 화면 연출 효과음 자리(uiSlots.ts): UI_SLOT_FILES에 있는 파일만 내려받고, 나머지는 처음부터 기존 효과음으로 대신한다
  const slotBuffers: Partial<Record<UiSlotName, AudioBuffer>> = {};
  const loadSlots = (): void => {
    for (const name of UI_SLOT_FILES) {
      fetch(resolveAssetUrl(uiSlotPath(name)))
        .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.arrayBuffer(); })
        .then((b) => ctx.decodeAudioData(b))
        .then((buf) => { slotBuffers[name] = buf; })
        .catch(() => { /* 파일이 아직 없다: 대체 효과음 */ });
    }
  };
  if (opts.deferNetwork) void opts.deferNetwork.then(() => { netOpen = true; loadSfx(); loadSlots(); });
  else { loadSfx(); loadSlots(); }

  // ── 목소리 풀
  const vGain: GainNode[] = [];
  const vPan: StereoPannerNode[] = [];
  const vSrc: (AudioBufferSourceNode | null)[] = [];
  const vEnd = new Float64Array(VOICES);
  const vId = new Int16Array(VOICES).fill(-1);
  let impactAt = -Infinity;
  for (let i = 0; i < VOICES; i++) {
    const g = ctx.createGain();
    const p = ctx.createStereoPanner();
    g.connect(p);
    p.connect(sfxBus);
    vGain.push(g); vPan.push(p); vSrc.push(null);
  }

  // ── 상태
  let seed = 0x9e3779b9 ^ ((performance.now() * 1000) | 0);
  const rand = (): number => {
    // xorshift32(할당 없음)
    seed ^= seed << 13; seed ^= seed >>> 17; seed ^= seed << 5;
    return ((seed >>> 0) % 1_000_000) / 1_000_000;
  };
  const st: DecideState = { rand, lastHit: -1 };
  const cues = new CueList(6);
  const limits = new VoiceLimiter(GROUP_LIMITS);
  const counts = new Uint32Array(SFX_NAMES.length);
  let played = 0, limited = 0, stolen = 0, skippedLocked = 0;
  let cursor = view.events.written;
  let unlocked = false;
  let hidden = document.visibilityState === 'hidden';
  let duckStart = Infinity, duckEnd = -1;
  let clock = 0;
  let prevMobState = new Uint8Array(view.mobs.capacity);
  const bgm = new BgmPlayer(ctx, bgmBus);
  bgm.setHidden(hidden);
  const ambience = new AmbiencePlayer(ctx, sfxBus, buffers);
  // 절차 효과음(c019): 장비 줍기·대장간·전설 고유 효과·지역3·4 위험·보스 새 패턴 경고. 효과음 버스로 합쳐 볼륨·음소거·리미터를 같이 받는다
  const audible = (): boolean => canPlay() && audioVolumes().master > 0 && audioVolumes().sfx > 0;
  const synth = new SynthEngine(ctx, sfxBus, () => audible());
  // 합성 층 오류는 게임·기존 효과음을 멈추지 않는다: 첫 오류에서 합성 층만 끄고 경고를 한 번 남긴다
  let synthOn = true;
  const synthFail = (err: unknown): void => {
    if (!synthOn) return;
    synthOn = false;
    try { synth.stop(); synth.silence(); } catch { /* 이미 망가짐 */ }
    console.warn('[audio] 절차 효과음 층을 껐습니다(오류):', err);
  };
  // 은행 워밍: 유휴 콜백이 있으면 유휴 시간에, 없으면 렌더 프레임마다 4ms 예산(update)
  try { synth.startIdleWarm(); } catch (err) { synthFail(err); }
  // 레이드 절차음(잿불 비룡·직업 AI·반복음·징글): 레이드 판에서만 은행을 채운다. 오류가 나면 이 층만 끈다
  const raidSfx = new RaidAudioEngine(ctx, sfxBus, () => audible());
  let raidOn = true;
  const raidFail = (err: unknown): void => {
    if (!raidOn) return;
    raidOn = false;
    try { raidSfx.silence(); } catch { /* 이미 망가짐 */ }
    console.warn('[audio] 레이드 효과음 층을 껐습니다(오류):', err);
  };

  /** 그 소리의 변형 파일이 하나라도 로드됐는가(합성 대체음 판단용). vbufs 구멍은 건너뛴다 */
  const hasSfxFile = (id: number): boolean => { const vs = vbufs[id]; if (!vs) return false; for (let i = 0; i < vs.length; i++) if (vs[i]) return true; return false; };

  const play = (c: Cue): void => {
    // 변형 고르기(직전과 다른 것). 내려온 변형 중에서만 고르고, 하나도 없을 때만 무음(0번 파일 실패여도 다른 변형이 있으면 재생)
    const vs = vbufs[c.id];
    const v = pickLoadedVariant(vs, lastVariant[c.id], rand());
    if (v < 0) return;
    const buf = vs[v];
    lastVariant[c.id] = v;
    const def = SFX_BY_ID[c.id];
    const t = ctx.currentTime;
    // 같은 소리가 MAX_SAME_SFX개 넘게 울리고 있으면 그중 가장 먼저 끝나는 것을, 아니면 가장 먼저 끝나는(또는 이미 끝난) 목소리를 쓴다
    let vi = 0, best = Infinity, same = 0, sameVi = -1, sameBest = Infinity;
    for (let i = 0; i < VOICES; i++) {
      if (vEnd[i] < best) { best = vEnd[i]; vi = i; }
      if (vId[i] === c.id && vEnd[i] > t) { same++; if (vEnd[i] < sameBest) { sameBest = vEnd[i]; sameVi = i; } }
    }
    if (same >= MAX_SAME_SFX && sameVi >= 0) vi = sameVi;
    const old = vSrc[vi];
    if (old && vEnd[vi] > t) { try { old.stop(); } catch { /* 이미 끝남 */ } stolen++; }
    let rate = c.rate * (def.jitter ? 1 + (jitterRate(rand()) - 1) * c.jitter : 1);
    let db = def.gainDb + c.gainDb + (def.jitter ? (rand() * 2 - 1) * GAIN_JITTER_DB * c.jitter : 0);
    let pan = 0;
    if (c.positional) {
      const dx = c.x - view.hero.x, dz = c.z - view.hero.z;
      pan = panFor(dx);
      db += distanceDb(Math.sqrt(dx * dx + dz * dz));
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    if (c.sweep === Sweep.Rise) {
      src.playbackRate.setValueAtTime(0.55 * rate, t);
      src.playbackRate.linearRampToValueAtTime(1.5 * rate, t + 0.6);
      rate = 0.55 * rate;
    } else src.playbackRate.value = rate;
    vGain[vi].gain.cancelScheduledValues(t);
    vGain[vi].gain.setValueAtTime(dbToGain(db), t);
    vPan[vi].pan.setValueAtTime(pan, t);
    src.connect(vGain[vi]);
    src.start(t);
    vSrc[vi] = src;
    vId[vi] = c.id;
    vEnd[vi] = t + buf.duration / Math.max(0.3, rate);
    // 큰 소리(묶음 Big, 음량 -3dB 이상)는 BGM을 잠깐 눌러 앞으로 낸다
    if (impactDucks(isBigGroup(def.group), def.gainDb + c.gainDb, audioVolumes())) impactAt = clock;
    counts[c.id]++;
    played++;
  };

  /** 효과음 자리 파일 재생(가장 먼저 끝나는 목소리 사용, 위치·피치 흔들림 없음) */
  const playSlot = (buf: AudioBuffer, db: number): void => {
    if (!limits.allow(Group.Misc, clock)) { limited++; return; }
    const t = ctx.currentTime;
    let vi = 0, best = Infinity;
    for (let i = 0; i < VOICES; i++) if (vEnd[i] < best) { best = vEnd[i]; vi = i; }
    const old = vSrc[vi];
    if (old && vEnd[vi] > t) { try { old.stop(); } catch { /* 이미 끝남 */ } stolen++; }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    vGain[vi].gain.cancelScheduledValues(t);
    vGain[vi].gain.setValueAtTime(dbToGain(db), t);
    vPan[vi].pan.setValueAtTime(0, t);
    src.connect(vGain[vi]);
    src.start(t);
    vSrc[vi] = src;
    vId[vi] = -1;
    vEnd[vi] = t + buf.duration;
    played++;
  };

  const emit = (): void => {
    const now = clock;
    for (let i = 0; i < cues.count; i++) {
      const c = cues.items[i];
      if (!limits.allow(c.group >= 0 ? c.group : SFX_BY_ID[c.id].group, now)) { limited++; continue; }
      play(c);
    }
    cues.clear();
  };

  const canPlay = (): boolean => unlocked && !hidden && ctx.state === 'running';

  const onEvent = (e: GameEvent): void => {
    if (e.type === Ev.AwakenPhase) {
      if (e.a === AwakenPhase.Gather) { duckStart = clock; duckEnd = -1; }
      else if (e.a === AwakenPhase.None && duckEnd < 0 && Number.isFinite(duckStart)) duckEnd = clock;
    }
    if (!canPlay()) { skippedLocked++; return; }
    // 메뉴가 없는 곳에서 낸 요청(no_menu)은 조용히 넘긴다. 결과 코드는 프로필 뷰가 갖고 있다
    if (e.type === Ev.Trade && e.a === 0 && view.profile.last.code === 'no_menu') return;
    const theme = view.map.hazardTheme ?? 'ember';
    const hero = view.heroId ?? 'kael';
    const raid = !!view.raid?.active;
    decideEvent(e, cues, st, view.boss.kind, theme, hero, raid);
    emit();
    if (raidOn) {
      try {
        raidSfx.onEvent(e, raid);
        if (raid && isHeavy(e)) raidSfx.duck();
      } catch (err) { raidFail(err); }
    }
    if (synthOn && !isWyvernEvent(e, view.boss.kind)) {
      try {
        synth.onEvent(e, theme, hasSfxFile);
        // c022 라이라 활·화살 소리(스킬 큐·기본 화살 발사·화살 타격·각성)
        const name = lyraCueOf(e, hero);
        if (name !== '') synth.cue(name, e.x, e.z);
      } catch (err) { synthFail(err); }
    }
  };

  // ── 자동재생 정책: 첫 사용자 입력에서 깨운다(이후에도 iOS 등이 다시 멈추면 다음 입력에서 다시 깨운다)
  const wake = (): void => {
    if (hidden) return;
    if (ctx.state !== 'running') {
      void ctx.resume().then(() => {
        unlocked = true; bgm.kick();
      }).catch(() => {});
    } else { unlocked = true; bgm.kick(); }
  };
  for (const ev of UNLOCK_EVENTS) window.addEventListener(ev, wake, { capture: true, passive: true });

  // 탭이 가려지면 일시정지, 돌아오면 다시 재생
  document.addEventListener('visibilitychange', () => {
    hidden = document.visibilityState === 'hidden';
    bgm.setHidden(hidden);
    if (hidden) { ambience.stop(); if (synthOn) synth.silence(); if (raidOn) raidSfx.silence(); void ctx.suspend().catch(() => {}); }
    else if (unlocked) void ctx.resume().then(() => bgm.kick()).catch(() => {});
  });

  // UI 클릭음(버튼·보상 카드). 게임 조작용 터치 버튼은 제외한다.
  document.addEventListener('click', (ev) => {
    const t = ev.target as Element | null;
    const hit = t && typeof t.closest === 'function' ? t.closest('button,[data-testid^="card-"]') : null;
    if (!hit || !canPlay()) return;
    const id = (hit as HTMLElement).dataset.testid ?? '';
    if (id.startsWith('touch-')) return;
    // 거래·장착·창 열기 버튼은 게임 이벤트(Ev.Trade·Ev.Menu)가 소리를 정한다(성공·실패가 달라서 클릭음을 겹치지 않는다)
    if ((hit as HTMLElement).dataset.sfx === 'event') return;
    cues.add(id.startsWith('card-') ? S.cardPick : CONFIRM_IDS.has(id) ? S.uiConfirm : S.uiClick);
    emit();
  }, { capture: true });

  // 화면 연출(CLEAR·카드 뽑기: ui/clearFx.ts·rewardCards.ts)이 보내는 효과음 신호. 이름은 SFX_NAMES에 있는 것만 받는다
  window.addEventListener('dh-ui-sfx', (ev) => {
    const name = (ev as CustomEvent<unknown>).detail;
    if (typeof name !== 'string' || !canPlay()) return;
    if (isUiSlot(name)) {
      const buf = slotBuffers[name];
      if (buf) { playSlot(buf, UI_SLOTS[name].gainDb); return; }
      cues.add(S[UI_SLOTS[name].fallback]);
      emit();
      return;
    }
    if (!Object.prototype.hasOwnProperty.call(S, name)) return;
    cues.add((S as Readonly<Record<string, number>>)[name]);
    emit();
  });

  // 볼륨이 바뀌면 바로 적용, 효과음 슬라이더는 미리듣기 한 번
  let previewAt = -1;
  onVolumeChange((kind) => {
    applyVolumes();
    if (audioVolumes().master === 0 || audioVolumes().sfx === 0) { ambience.stop(); if (synthOn) synth.silence(); if (raidOn) raidSfx.silence(); }
    if (kind === 'sfx' && canPlay() && clock - previewAt > 0.15) {
      previewAt = clock;
      cues.add(S.hit0);
      emit();
    }
  });

  // ── QA 훅(재생 통계·레벨 측정·녹음)
  let analyser: AnalyserNode | null = null;
  let probe: Float32Array<ArrayBuffer> | null = null;
  let peakMax = 0, rmsMax = 0, loudFrames = 0, meterFrames = 0;
  if (opts.qa) {
    analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    trim.connect(analyser);
    probe = new Float32Array(analyser.fftSize);
    let recDest: MediaStreamAudioDestinationNode | null = null;
    let longRec: MediaRecorder | null = null;
    const pending: Blob[] = [];
    const recMime = (): string => (MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm');
    const ensureDest = (): MediaStreamAudioDestinationNode => {
      if (!recDest) { recDest = ctx.createMediaStreamDestination(); trim.connect(recDest); }
      return recDest;
    };
    const toB64 = async (blobs: Blob[], mime: string): Promise<string> => {
      const bytes = new Uint8Array(await new Blob(blobs, { type: mime }).arrayBuffer());
      let s = '';
      for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)));
      return btoa(s);
    };
    (window as unknown as { __DHA__: unknown }).__DHA__ = {
      state: () => ctx.state,
      unlocked: () => unlocked,
      stats: () => {
        const byName: Record<string, number> = {};
        SFX_NAMES.forEach((n, i) => { if (counts[i] > 0) byName[n] = counts[i]; });
        return {
          state: ctx.state, unlocked, sampleRate: ctx.sampleRate, loaded, loadFailed, total: SFX_NAMES.length,
          played, limited, stolen, skippedLocked, byName, ambience: ambience.stats(), synth: { on: synthOn, ...synth.stats() },
          raid: { on: raidOn, ...raidSfx.stats() },
          bgm: {
            track: bgm.track >= 0 ? ALL_TRACKS[bgm.track] : null,
            playback: ALL_TRACKS.map((_, i) => bgm.playbackOf(i)),
            status: ALL_TRACKS.map((n, i) => n + ':' + bgm.statusOf(i)),
            gain: ALL_TRACKS.map((_, i) => Math.round(bgm.gainOf(i) * 1000) / 1000),
            time: ALL_TRACKS.map((_, i) => Math.round(bgm.timeOf(i) * 100) / 100),
          },
          duckDb: duckDb(clock - duckStart, duckEnd < 0 ? -1 : clock - duckEnd),
          meter: { peakMax, rmsMax, loudFrames, meterFrames },
          volumes: { ...audioVolumes() },
        };
      },
      /** ms 동안 마스터 출력을 녹음해 base64(webm/opus)로 돌려준다 */
      record: (ms: number): Promise<{ mime: string; b64: string }> => {
        const mime = recMime();
        const rec = new MediaRecorder(ensureDest().stream, { mimeType: mime, audioBitsPerSecond: 128000 });
        const chunks: Blob[] = [];
        rec.ondataavailable = (ev) => { if (ev.data.size > 0) chunks.push(ev.data); };
        return new Promise((resolve) => {
          rec.onstop = async () => resolve({ mime, b64: await toB64(chunks, mime) });
          rec.start(1000);
          setTimeout(() => rec.stop(), ms);
        });
      },
      /** 긴 녹음 시작(1초 조각). recDrain으로 쌓인 조각을 이어 받는다(페이지가 닫혀도 마지막 회수분까지 남는다). */
      recStart: (): string => {
        if (longRec) return longRec.mimeType;
        longRec = new MediaRecorder(ensureDest().stream, { mimeType: recMime(), audioBitsPerSecond: 128000 });
        longRec.ondataavailable = (ev) => { if (ev.data.size > 0) pending.push(ev.data); };
        longRec.start(1000);
        return longRec.mimeType;
      },
      recDrain: async (): Promise<string> => {
        if (pending.length === 0) return '';
        const blobs = pending.splice(0, pending.length);
        return toB64(blobs, recMime());
      },
    };
  }

  return {
    update(frameDt: number) {
      // 긴 프레임(로딩 멈춤·느린 기기 0.25초 이상)도 0.25초까지는 센다. 예전에는 0으로 버려 시계가 멈추면 묶음별 동시 시작 제한 창이
      // 풀리지 않아 그 뒤 소리가 계속 버려졌다(2026-11-03 헤드리스 실측: 4fps 아래에서 46종 중 37종 재생 0, limited 163)
      clock += frameDt > 0 ? Math.min(frameDt, 0.25) : 0;
      cursor = view.events.readFrom(cursor, onEvent);
      if (synthOn) { try { synth.flush(view.hero.x, view.hero.z); } catch (err) { synthFail(err); } }
      // 레이드: 브레스 불길 틱·날갯짓·불 장판 지글·은행 워밍(레이드 판일 때만)·모인 소리
      if (raidOn) { try { raidSfx.update(view, frameDt); } catch (err) { raidFail(err); } }

      // 몹 공격: 전용 이벤트가 없어 상태가 Attack으로 바뀌는 순간을 감지한다
      const m = view.mobs;
      if (prevMobState.length !== m.capacity) prevMobState = new Uint8Array(m.capacity);
      const play2 = canPlay() && combatScene(view.flow.scene);
      for (let i = 0; i < m.capacity; i++) {
        const s = m.state[i];
        if (play2 && s === MobState.Attack && prevMobState[i] !== MobState.Attack) cues.add(S.mobAttack, 0, 1, m.x[i], m.z[i]);
        prevMobState[i] = s;
        if (cues.count >= cues.items.length) emit();
      }
      if (cues.count > 0) emit();

      ambience.update(view, audible());
      // 절차 효과음 은행(유휴 콜백이 없는 브라우저): 직전 프레임이 20ms 넘으면 건너뛰고, 아니면 4ms 예산 안에서 렌더 단계를 잇는다
      if (synthOn) { try { synth.warmStep(frameDt); } catch (err) { synthFail(err); } }
      if (netOpen) bgm.select(chooseTrack(view.flow.scene, view.boss.state, !!view.raid?.active, view.flow.scene === Scene.Result ? regionOfResult(view.result.stageId, view.flow.mapId) : regionOfResult(undefined, view.flow.mapId), liveTrackOf(liveMusic.key)));
      if (unlocked) bgm.poll(performance.now() / 1000);
      duck.gain.value = dbToGain(Math.min(duckDb(clock - duckStart, duckEnd < 0 ? -1 : clock - duckEnd), impactDuckDb(clock - impactAt)));

      if (analyser && probe && unlocked) {
        analyser.getFloatTimeDomainData(probe);
        let pk = 0, sum = 0;
        for (let i = 0; i < probe.length; i++) { const v = probe[i]; const a = v < 0 ? -v : v; if (a > pk) pk = a; sum += v * v; }
        const rms = Math.sqrt(sum / probe.length);
        if (pk > peakMax) peakMax = pk;
        if (rms > rmsMax) rmsMax = rms;
        if (rms > 0.01) loudFrames++;
        meterFrames++;
      }
    },
    setVolume: setAudioVolume,
    volumes: audioVolumes,
  };
}
